import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  ApiRequestError,
  grantSupportAccess,
  listSupportAccessGrants,
  revokeSupportAccess,
  toastApiError,
} from '@/lib/api'
import { absoluteTime } from '@/lib/datetime'
import type { SupportAccessGrant } from '@/lib/schemas'
import { useAuthStore } from '@/stores/auth-store'
import { useBidsStore } from '@/stores/bids-store'

// ===================
// Constants
// ===================

const MAX_DAYS = 14
const DEFAULT_DAYS = 7
const PAST_GRANTS_SHOWN = 5
const WHOLE_ORG = 'org'
const DAY_OPTIONS = Array.from({ length: MAX_DAYS }, (_, i) => i + 1)

type FieldErrors = Partial<Record<'days' | 'bidId' | 'allowsReplay' | 'reason', string>>

// ===================
// Helpers
// ===================

function scopeLabel(grant: SupportAccessGrant): string {
  if (!grant.bidId) return 'Whole organisation'
  return grant.bidTitle ?? 'One bid'
}

function granterLabel(grant: SupportAccessGrant, currentUserId: string | undefined): string {
  if (currentUserId && grant.grantedBy === currentUserId) return 'You'
  return grant.grantedByName ?? grant.grantedByEmail ?? 'An admin'
}

/** Map a 400's `details.errors` ({ field: [msg] }) onto the dialog's fields. */
function fieldErrorsFrom(err: ApiRequestError): { fields: FieldErrors; general: string | null } {
  const fields: FieldErrors = {}
  const raw = err.details?.['errors']
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      const message = Array.isArray(value) ? value.find((v) => typeof v === 'string') : undefined
      if (typeof message !== 'string') continue
      if (key === 'days' || key === 'bidId' || key === 'allowsReplay' || key === 'reason') {
        fields[key] = message
      }
    }
  }
  const hasFieldErrors = Object.keys(fields).length > 0
  return { fields, general: hasFieldErrors ? null : err.message }
}

// ===================
// Component
// ===================

export function SupportAccessSection() {
  const currentUserId = useAuthStore((s) => s.user?.id)
  const bids = useBidsStore((s) => s.bids)
  const ensureAllBids = useBidsStore((s) => s.ensureAllBids)

  const [grants, setGrants] = useState<SupportAccessGrant[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [isGrantOpen, setIsGrantOpen] = useState(false)
  const [days, setDays] = useState(DEFAULT_DAYS)
  const [scope, setScope] = useState<string>(WHOLE_ORG)
  const [allowsReplay, setAllowsReplay] = useState(false)
  const [reason, setReason] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [revokeTarget, setRevokeTarget] = useState<SupportAccessGrant | null>(null)
  const [isRevoking, setIsRevoking] = useState(false)

  const loadGrants = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      setGrants(await listSupportAccessGrants())
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load support access')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadGrants()
  }, [loadGrants])

  // The bid picker only needs the list once the dialog is open.
  useEffect(() => {
    if (isGrantOpen) void ensureAllBids()
  }, [isGrantOpen, ensureAllBids])

  const activeGrants = useMemo(() => grants.filter((g) => g.status === 'active'), [grants])
  const pastGrants = useMemo(
    () => grants.filter((g) => g.status !== 'active').slice(0, PAST_GRANTS_SHOWN),
    [grants],
  )
  const bidOptions = useMemo(
    () =>
      bids
        .filter((b) => !b.archived)
        .slice()
        .sort((a, b) => a.title.localeCompare(b.title)),
    [bids],
  )

  const resetForm = () => {
    setDays(DEFAULT_DAYS)
    setScope(WHOLE_ORG)
    setAllowsReplay(false)
    setReason('')
    setFieldErrors({})
    setFormError(null)
  }

  // A "reason required" error goes as soon as there is a reason; it stays
  // while the box is still blank.
  const handleReasonChange = (value: string) => {
    setReason(value)
    if (value.trim()) {
      setFieldErrors((prev) => {
        if (!prev.reason) return prev
        const next = { ...prev }
        delete next.reason
        return next
      })
    }
  }

  const handleGrantOpenChange = (open: boolean) => {
    if (isSubmitting) return
    setIsGrantOpen(open)
    if (!open) resetForm()
  }

  const handleGrant = async () => {
    const trimmedReason = reason.trim()
    if (!trimmedReason) {
      setFieldErrors({ reason: 'A reason is required' })
      setFormError(null)
      return
    }
    setIsSubmitting(true)
    setFieldErrors({})
    setFormError(null)
    try {
      const grant = await grantSupportAccess({
        days,
        reason: trimmedReason,
        bidId: scope === WHOLE_ORG ? null : scope,
        allowsReplay,
      })
      setGrants((prev) => [grant, ...prev.filter((g) => g.id !== grant.id)])
      setIsGrantOpen(false)
      resetForm()
      toast.success('Support access granted')
    } catch (err) {
      if (err instanceof ApiRequestError && err.statusCode === 400) {
        const { fields, general } = fieldErrorsFrom(err)
        setFieldErrors(fields)
        setFormError(general)
      } else if (err instanceof ApiRequestError && err.statusCode === 404) {
        setFieldErrors({ bidId: 'That bid could not be found. Choose another.' })
      } else {
        toastApiError(err, 'Failed to grant support access')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRevoke = async () => {
    if (!revokeTarget) return
    setIsRevoking(true)
    try {
      const updated = await revokeSupportAccess(revokeTarget.id)
      setGrants((prev) => prev.map((g) => (g.id === updated.id ? updated : g)))
      setRevokeTarget(null)
      toast.success('Support access revoked')
    } catch (err) {
      toastApiError(err, 'Failed to revoke support access')
    } finally {
      setIsRevoking(false)
    }
  }

  return (
    <section className="space-y-4" aria-labelledby="support-access-heading">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 id="support-access-heading" className="text-sm font-medium">
            Support access
          </h2>
          <p className="text-meta mt-1">
            Lets the BidContext team see the AI's working for this organisation, to investigate a
            problem. Access ends automatically.
          </p>
        </div>
        <Button
          variant="outline"
          className="shrink-0"
          onClick={() => setIsGrantOpen(true)}
          disabled={isLoading || !!loadError}
        >
          Grant access
        </Button>
      </div>

      {isLoading ? (
        <div className="text-meta flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading support access…
        </div>
      ) : loadError ? (
        <div className="flex items-center gap-2">
          <p className="text-sm text-destructive">Couldn't load support access.</p>
          <Button variant="ghost" size="sm" onClick={() => void loadGrants()}>
            Try again
          </Button>
        </div>
      ) : (
        <>
          {activeGrants.length === 0 ? (
            <p className="text-meta">No support access granted.</p>
          ) : (
            <ul className="divide-y divide-border" aria-label="Current support access">
              {activeGrants.map((grant) => (
                <li key={grant.id} className="flex items-start justify-between gap-4 py-3 first:pt-0">
                  <div className="min-w-0 space-y-0.5">
                    <p className="text-sm">{scopeLabel(grant)}</p>
                    <p className="text-meta">
                      Granted by {granterLabel(grant, currentUserId)} · Until{' '}
                      {absoluteTime(grant.expiresAt)} ·{' '}
                      {grant.allowsReplay ? 'Replay allowed' : 'No replay'}
                    </p>
                    <p className="text-meta truncate" title={grant.reason}>
                      Reason: {grant.reason}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 hover:text-destructive"
                    onClick={() => setRevokeTarget(grant)}
                    aria-label={`Revoke support access for ${scopeLabel(grant)}`}
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {pastGrants.length > 0 && (
            <div className="space-y-1">
              <h3 className="text-meta font-medium">Past access</h3>
              <ul className="space-y-1" aria-label="Past support access">
                {pastGrants.map((grant) => (
                  <li key={grant.id} className="text-meta">
                    {scopeLabel(grant)} · granted by {granterLabel(grant, currentUserId)} ·{' '}
                    {grant.status === 'revoked' && grant.revokedAt
                      ? `revoked ${absoluteTime(grant.revokedAt)}`
                      : `ended ${absoluteTime(grant.expiresAt)}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Grant dialog */}
      <Dialog open={isGrantOpen} onOpenChange={handleGrantOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant support access</DialogTitle>
            <DialogDescription>
              The BidContext team can see the AI's working until access ends. You can revoke it at
              any time.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="support-access-days">How long</Label>
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger id="support-access-days" aria-label="How long">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAY_OPTIONS.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d} {d === 1 ? 'day' : 'days'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.days && <p className="text-xs text-destructive">{fieldErrors.days}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="support-access-scope">What they can see</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger id="support-access-scope" aria-label="What they can see">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={WHOLE_ORG}>Whole organisation</SelectItem>
                  {bidOptions.map((bid) => (
                    <SelectItem key={bid.id} value={bid.id}>
                      {bid.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.bidId && <p className="text-xs text-destructive">{fieldErrors.bidId}</p>}
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="support-access-replay">Also allow replay</Label>
                <p className="text-meta">
                  Lets the team re-run a step to reproduce a problem. Nothing is written to your
                  bids.
                </p>
                {fieldErrors.allowsReplay && (
                  <p className="text-xs text-destructive">{fieldErrors.allowsReplay}</p>
                )}
              </div>
              <Switch
                id="support-access-replay"
                checked={allowsReplay}
                onCheckedChange={setAllowsReplay}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="support-access-reason">Reason</Label>
              <Textarea
                id="support-access-reason"
                value={reason}
                onChange={(e) => handleReasonChange(e.target.value)}
                placeholder="What should the team look into?"
                maxLength={1000}
                rows={3}
                aria-invalid={!!fieldErrors.reason}
              />
              {fieldErrors.reason && (
                <p className="text-xs text-destructive">{fieldErrors.reason}</p>
              )}
            </div>

            {formError && <p className="text-xs text-destructive">{formError}</p>}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => handleGrantOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button className="gap-2" onClick={() => void handleGrant()} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Grant access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation */}
      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={(open) => {
          if (!open && !isRevoking) setRevokeTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke support access?</AlertDialogTitle>
            <AlertDialogDescription>
              The BidContext team will lose access to{' '}
              {revokeTarget && !revokeTarget.bidId
                ? 'this organisation'
                : (revokeTarget?.bidTitle ?? 'this bid')}{' '}
              straight away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRevoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleRevoke()
              }}
              disabled={isRevoking}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRevoking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
